import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
    LayoutDashboard,
    Users,
    Award,
    QrCode,
    Calendar,
    BookOpen,
    Receipt,
    Settings,
    FileText,
    LogOut,
    Menu,
    X,
    Heart,
    ChevronDown,
    ChevronRight,
    Package,
    Palette,
    Type,
    Code,
    Mail,
    AlertTriangle,
    Server,
    ExternalLink,
    GraduationCap,
    Scale,
    HardDrive,
    RefreshCw,
    Pencil,
    Shield,
    Clock,
    Bell,
    User,
    HelpCircle,
    Wrench,
    Ban,
    LifeBuoy,
    Folder,
    ShieldAlert,
    Activity,
    Image as ImageIcon,
    Building2,
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { AppFooter } from '../common/AppFooter';
import { MyProfileDialog } from '../common/MyProfileDialog';
import { ReportIssueDialog } from '../common/ReportIssueDialog';
import { adminUserApi, settingsApi, cancellationsApi, ticketsApi, accountDeletionApi, healthApi } from '../../lib/api';

const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/instructors', icon: Users, label: 'Akademie-Mitglieder' },
    { path: '/admin/licenses', icon: Award, label: 'Lizenzen' },
    { path: '/admin/qr-scans', icon: QrCode, label: 'QR-Abrufe' },
    { path: '/admin/seminars', icon: Calendar, label: 'Fortbildungen' },
    { path: '/admin/firmen', icon: Building2, label: 'Firmen' },
    { path: '/admin/referents', icon: User, label: 'Referenten' },
    { path: '/admin/invoices', icon: Receipt, label: 'Buchhaltung' },
    { path: '/admin/cancellations', icon: Ban, label: 'Stornierungen' },
    { path: '/admin/account-deletions', icon: ShieldAlert, label: 'Konto-Löschungen' },
    { path: '/admin/faq', icon: HelpCircle, label: 'Hilfe & FAQ' },
    { path: '/admin/system-health', icon: Activity, label: 'System-Health' },
];

// Collapsible sections inside Einstellungen. Each section has items + an icon.
const settingsSections = [
    {
        key: 'appearance',
        label: 'Darstellung',
        icon: Palette,
        items: [
            { tab: 'design', icon: Palette, label: 'Design' },
            { tab: 'logos', icon: FileText, label: 'Logos' },
            { tab: 'login-page', icon: ImageIcon, label: 'Login-Seite' },
            { tab: 'fonts', icon: Type, label: 'Schriften' },
            { tab: 'widget', icon: Code, label: 'Widget' },
        ],
    },
    {
        key: 'documents',
        label: 'Dokumente & Belege',
        icon: FileText,
        items: [
            { tab: 'bank', icon: Receipt, label: 'Belege & Absender' },
            { tab: 'templates', icon: FileText, label: 'PDF-Vorlagen' },
            { tab: 'legal', icon: Scale, label: 'Rechtliches' },
        ],
    },
    {
        key: 'training',
        label: 'Lizenzen & Seminare',
        icon: GraduationCap,
        items: [
            { tab: 'prerequisites', icon: GraduationCap, label: 'Vorkenntnisse' },
            { tab: 'renewal-rules', icon: RefreshCw, label: 'Verlängerungsregeln' },
            { tab: 'cancellations', icon: Ban, label: 'Stornierungen' },
        ],
    },
    {
        key: 'accounting',
        label: 'Buchhaltung',
        icon: Receipt,
        items: [
            { tab: 'expense-categories', icon: Folder, label: 'Ausgabenkategorien' },
        ],
    },
    {
        key: 'mail',
        label: 'Mail',
        icon: Mail,
        items: [
            { tab: 'smtp', icon: Server, label: 'SMTP-Zugang' },
            { tab: 'email-templates', icon: FileText, label: 'Mail-Vorlagen', path: '/admin/settings/email-templates' },
            { tab: 'email-signature', icon: Pencil, label: 'Mail-Signatur' },
            { tab: 'notifications', icon: Bell, label: 'System-Benachrichtigungen' },
            { tab: 'reminders', icon: AlertTriangle, label: 'Mail-Erinnerungen' },
        ],
    },
    {
        key: 'system',
        label: 'System',
        icon: Server,
        items: [
            { tab: 'php-version', icon: Package, label: 'Version & Deployment' },
            { tab: 'general', icon: AlertTriangle, label: 'Allgemein' },
            { tab: 'audit-log', icon: Shield, label: 'Admin-Aktivitäten' },
            { tab: 'dropbox', icon: HardDrive, label: 'Dropbox' },
            { tab: 'system', icon: Server, label: 'System-Status' },
            { tab: 'session', icon: Clock, label: 'Auto-Logout' },
        ],
    },
    {
        key: 'user-management',
        label: 'Benutzer & Rechte',
        icon: Shield,
        superAdminOnly: true,
        items: [
            { tab: 'user-management', icon: Users, label: 'Benutzerverwaltung' },
            { tab: 'admin-permissions', icon: Shield, label: 'Admin-Berechtigungen' },
        ],
    },
];

export function AdminLayout({ children }) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [headerLogo, setHeaderLogo] = useState(null);
    const [profileOpen, setProfileOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [maintenance, setMaintenance] = useState({ enabled: false, title: '' });
    const [cancelSummary, setCancelSummary] = useState({ pending: 0, refund_open: 0 });
    const [deletionPending, setDeletionPending] = useState(0);
    const [openTicketCount, setOpenTicketCount] = useState(0);
    const [allowedSections, setAllowedSections] = useState(null); // null = laden / array = filter / 'all' = super_admin
    const [healthSummary, setHealthSummary] = useState(null);
    const navRef = React.useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const isSuperAdmin = user?.role === 'super_admin';

    const BACKEND_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL || "");

    useEffect(() => {
        // Tab-Berechtigungen einmalig laden
        adminUserApi.getMyPermissions()
            .then(r => setAllowedSections(r.data?.all ? 'all' : (r.data?.allowed_tabs || [])))
            .catch(() => setAllowedSections([]));
    }, []);

    // Wartungsmodus-Status (Polling alle 30s für Live-Update nach Toggle)
    useEffect(() => {
        const fetchStatus = () => {
            settingsApi.getMaintenance()
                .then(r => setMaintenance({ enabled: !!r.data.enabled, title: r.data.title || 'Kurze Wartungspause' }))
                .catch(() => { /* ignore */ });
            cancellationsApi.summary()
                .then(r => setCancelSummary({ pending: r.data.pending || 0, refund_open: r.data.refund_open || 0 }))
                .catch(() => { /* ignore */ });
            ticketsApi.countOpen()
                .then(r => setOpenTicketCount(r.data.count || 0))
                .catch(() => { /* ignore */ });
            accountDeletionApi.countPending()
                .then(r => setDeletionPending(r.data?.pending || 0))
                .catch(() => { /* ignore */ });
            healthApi.summary()
                .then(r => setHealthSummary(r.data || null))
                .catch(() => { /* ignore */ });
        };
        fetchStatus();
        const id = setInterval(fetchStatus, 30000);
        return () => clearInterval(id);
    }, [location.pathname]);

    const sectionAllowed = (key) => allowedSections === 'all' || (Array.isArray(allowedSections) && allowedSections.includes(key));
    const visibleSettingsSections = settingsSections.filter(s => sectionAllowed(s.key));
    const settingsVisible = isSuperAdmin || visibleSettingsSections.length > 0;

    useEffect(() => {
        // Check if header logo exists
        const img = new Image();
        img.onload = () => setHeaderLogo(`${BACKEND_URL}/api/settings/logos/header?t=${Date.now()}`);
        img.onerror = () => setHeaderLogo(null);
        img.src = `${BACKEND_URL}/api/settings/logos/header`;
    }, [BACKEND_URL]);

    const isSettingsPage = location.pathname.startsWith('/admin/settings');
    const effectiveSettingsOpen = settingsOpen || isSettingsPage;

    const getActiveTab = () => {
        const parts = location.pathname.split('/');
        if (parts.length >= 4 && parts[2] === 'settings') return parts[3];
        return 'php-version';
    };
    const activeTab = getActiveTab();

    // Find which settings section the current tab lives in
    const activeSectionKey = React.useMemo(() => {
        for (const sec of settingsSections) {
            if (sec.items.some(it => it.tab === activeTab)) return sec.key;
        }
        return null;
    }, [activeTab]);

    // Track open sections (persisted so they survive AdminLayout remounts across pages)
    const [openSections, setOpenSections] = useState(() => {
        try {
            const raw = sessionStorage.getItem('bgpr_nav_sections');
            if (raw) return JSON.parse(raw);
        } catch { /* ignore */ }
        return {};
    });
    useEffect(() => {
        if (activeSectionKey) {
            setOpenSections((prev) => (prev[activeSectionKey] ? prev : { ...prev, [activeSectionKey]: true }));
        }
    }, [activeSectionKey]);
    useEffect(() => {
        try { sessionStorage.setItem('bgpr_nav_sections', JSON.stringify(openSections)); } catch { /* ignore */ }
    }, [openSections]);

    // Persist + restore sidebar scroll across navigations / AdminLayout remounts
    useEffect(() => {
        const node = navRef.current;
        if (!node) return;
        try {
            const y = parseInt(sessionStorage.getItem('bgpr_nav_scroll') || '0', 10);
            if (y) node.scrollTop = y;
        } catch { /* ignore */ }
        const onScroll = () => {
            try { sessionStorage.setItem('bgpr_nav_scroll', String(node.scrollTop)); } catch { /* ignore */ }
        };
        node.addEventListener('scroll', onScroll, { passive: true });
        return () => node.removeEventListener('scroll', onScroll);
    }, []);

    const toggleSection = (key) => {
        setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const isActive = (item) => {
        if (item.exact) return location.pathname === item.path;
        return location.pathname.startsWith(item.path);
    };

    return (
        <div className="min-h-screen bg-background">
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/50 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={cn(
                    'fixed top-0 left-0 z-50 h-full w-64 bg-sidebar transform transition-transform duration-300 lg:translate-x-0 flex flex-col',
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                )}
            >
                {/* Logo */}
                <div className="flex items-center gap-3 px-6 py-5 border-b border-white/10 shrink-0">
                    {headerLogo ? (
                        <img src={headerLogo} alt="Logo" className="h-9 w-auto object-contain" />
                    ) : (
                        <div className="p-2 bg-primary rounded-lg">
                            <Heart className="h-5 w-5 text-white" />
                        </div>
                    )}
                    <div>
                        <h1 className="text-lg font-bold font-heading text-white tracking-tight">BGPR-Akademie</h1>
                        <p className="text-xs text-white/60">Verwaltungssystem</p>
                    </div>
                    <button
                        className="ml-auto lg:hidden text-white/60 hover:text-white"
                        onClick={() => setSidebarOpen(false)}
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Navigation */}
                <nav ref={navRef} className="p-4 space-y-1 flex-1 overflow-y-auto scroll-smooth">
                    {navItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setSidebarOpen(false)}
                            className={cn(
                                'flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors',
                                isActive(item)
                                    ? 'bg-primary text-white'
                                    : 'text-white/70 hover:bg-sidebar-accent hover:text-white'
                            )}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                            <item.icon className="h-4 w-4" />
                            <span className="flex-1">{item.label}</span>
                            {item.path === '/admin/cancellations' && cancelSummary.pending > 0 && (
                                <span
                                    className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold rounded-full bg-rose-500 text-white"
                                    data-testid="cancellations-pending-badge"
                                >
                                    {cancelSummary.pending}
                                </span>
                            )}
                            {item.path === '/admin/account-deletions' && deletionPending > 0 && (
                                <span
                                    className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold rounded-full bg-rose-500 text-white"
                                    data-testid="account-deletions-pending-badge"
                                >
                                    {deletionPending}
                                </span>
                            )}
                            {item.path === '/admin/system-health' && healthSummary && healthSummary.overall !== 'ok' && (
                                <span
                                    className={cn(
                                        'ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold rounded-full text-white',
                                        healthSummary.overall === 'error' ? 'bg-rose-500' : 'bg-amber-500'
                                    )}
                                    data-testid="system-health-nav-badge"
                                    title={
                                        healthSummary.overall === 'error'
                                            ? `${healthSummary.error_count} Fehler erkannt`
                                            : `${healthSummary.warning_count} Warnung(en)`
                                    }
                                >
                                    {(healthSummary.error_count || 0) + (healthSummary.warning_count || 0)}
                                </span>
                            )}
                        </Link>
                    ))}

                    {/* Settings with collapsible sections */}
                    {settingsVisible && (
                    <div>
                        <button
                            onClick={() => {
                                if (!isSettingsPage) navigate('/admin/settings');
                                setSettingsOpen(!effectiveSettingsOpen);
                            }}
                            className={cn(
                                'flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors w-full',
                                isSettingsPage
                                    ? 'bg-primary text-white'
                                    : 'text-white/70 hover:bg-sidebar-accent hover:text-white'
                            )}
                            data-testid="nav-einstellungen"
                        >
                            <Settings className="h-4 w-4" />
                            <span className="flex-1 text-left">Einstellungen</span>
                            <ChevronRight className={cn(
                                'h-3.5 w-3.5 transition-transform duration-200',
                                effectiveSettingsOpen && 'rotate-90'
                            )} />
                        </button>

                        {/* Collapsible sections */}
                        {effectiveSettingsOpen && (
                            <div className="mt-1 ml-4 pl-3 border-l border-white/10 space-y-1">
                                {visibleSettingsSections.map((section) => {
                                    const isOpen = !!openSections[section.key];
                                    const hasActive = section.items.some(it => it.tab === activeTab);
                                    const SectionIcon = section.icon;
                                    return (
                                        <div key={section.key} className="select-none">
                                            <button
                                                type="button"
                                                onClick={() => toggleSection(section.key)}
                                                className={cn(
                                                    'w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[11px] font-semibold uppercase tracking-wider transition-colors',
                                                    hasActive
                                                        ? 'text-white bg-white/5'
                                                        : 'text-white/55 hover:text-white/80 hover:bg-white/5'
                                                )}
                                                data-testid={`nav-section-${section.key}`}
                                            >
                                                <SectionIcon className="h-3.5 w-3.5 opacity-70" />
                                                <span className="flex-1 text-left">{section.label}</span>
                                                <ChevronRight className={cn(
                                                    'h-3 w-3 transition-transform duration-200',
                                                    isOpen && 'rotate-90'
                                                )} />
                                            </button>
                                            {isOpen && (
                                                <div className="mt-0.5 mb-1 ml-3 pl-2 border-l border-white/5 space-y-0.5">
                                                    {section.items.map((sub) => (
                                                        <Link
                                                            key={sub.tab}
                                                            to={sub.path || `/admin/settings/${sub.tab}`}
                                                            onClick={() => setSidebarOpen(false)}
                                                            className={cn(
                                                                'flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors',
                                                                (sub.path ? location.pathname.startsWith(sub.path) : activeTab === sub.tab)
                                                                    ? 'bg-primary/90 text-white'
                                                                    : 'text-white/55 hover:bg-white/5 hover:text-white/90'
                                                            )}
                                                            data-testid={`nav-settings-${sub.tab}`}
                                                        >
                                                            <sub.icon className="h-3.5 w-3.5" />
                                                            {sub.label}
                                                        </Link>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    )}
                </nav>

                {/* User section with dropdown */}
                <div className="p-3 border-t border-white/10 shrink-0">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-white/80 hover:bg-white/5 hover:text-white transition-colors"
                                data-testid="sidebar-user-menu"
                            >
                                <div className="h-9 w-9 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                                    <span className="text-sm font-semibold text-white">
                                        {user?.first_name?.[0]}{user?.last_name?.[0]}
                                    </span>
                                </div>
                                <div className="flex-1 min-w-0 text-left">
                                    <p className="text-sm font-medium text-white truncate">
                                        {user?.first_name} {user?.last_name}
                                    </p>
                                    <p className="text-[11px] text-white/50 truncate">
                                        {isSuperAdmin ? 'Super-Admin' : 'Admin'}
                                    </p>
                                </div>
                                <ChevronRight className="h-3.5 w-3.5 text-white/40 shrink-0" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent side="right" align="end" className="w-56">
                            <DropdownMenuLabel>
                                <p className="font-medium">{user?.first_name} {user?.last_name}</p>
                                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setProfileOpen(true)} data-testid="sidebar-user-profile">
                                <User className="h-4 w-4 mr-2" />
                                Mein Profil & 2FA
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="sidebar-user-logout">
                                <LogOut className="h-4 w-4 mr-2" />
                                Abmelden
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </aside>

            {/* Main content */}
            <div className="lg:pl-64">
                <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-sm border-b">
                    <div className="flex items-center justify-between px-6 py-4">
                        <button
                            className="lg:hidden p-2 hover:bg-muted rounded-md"
                            onClick={() => setSidebarOpen(true)}
                            data-testid="mobile-menu-button"
                        >
                            <Menu className="h-5 w-5" />
                        </button>

                        <div className="flex-1" />

                        <button
                            type="button"
                            onClick={() => setReportOpen(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 mr-2 text-xs font-medium text-amber-700 hover:text-amber-900 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors"
                            data-testid="topbar-report-issue"
                            title="Fehler melden"
                        >
                            <AlertTriangle className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Fehler melden</span>
                        </button>

                        <Link
                            to="/admin/tickets"
                            className="relative inline-flex items-center gap-1.5 px-3 py-1.5 mr-2 text-xs font-medium text-muted-foreground hover:text-primary bg-muted/50 hover:bg-muted rounded-lg transition-colors"
                            data-testid="topbar-tickets-link"
                            title="Tickets &amp; Fehlermeldungen"
                        >
                            <LifeBuoy className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Tickets</span>
                            {openTicketCount > 0 && (
                                <span
                                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none ring-2 ring-white"
                                    data-testid="topbar-tickets-badge"
                                    aria-label={`${openTicketCount} offene Tickets`}
                                >
                                    {openTicketCount > 9 ? '9+' : openTicketCount}
                                </span>
                            )}
                        </Link>

                        <a
                            href="/seminare"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 mr-3 text-xs font-medium text-muted-foreground hover:text-primary bg-muted/50 hover:bg-muted rounded-lg transition-colors"
                            data-testid="quicklink-public-page"
                        >
                            <GraduationCap className="h-3.5 w-3.5" />
                            BGPR-Akademie
                            <ExternalLink className="h-3 w-3 opacity-70" />
                        </a>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="gap-2" data-testid="user-menu-button">
                                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                        <span className="text-sm font-medium text-primary">
                                            {user?.first_name?.[0]}{user?.last_name?.[0]}
                                        </span>
                                    </div>
                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel>
                                    <div>
                                        <p className="font-medium">{user?.first_name} {user?.last_name}</p>
                                        <p className="text-xs text-muted-foreground">{user?.email}</p>
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setProfileOpen(true)} data-testid="topbar-my-profile">
                                    <User className="h-4 w-4 mr-2" />
                                    Mein Profil & 2FA
                                </DropdownMenuItem>
                                {settingsVisible && (
                                    <DropdownMenuItem onClick={() => navigate('/admin/settings')}>
                                        <Settings className="h-4 w-4 mr-2" />
                                        Einstellungen
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                                    <LogOut className="h-4 w-4 mr-2" />
                                    Abmelden
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                {maintenance.enabled && (
                    <div
                        className="bg-amber-50 border-b border-amber-200 px-6 py-2.5 flex items-center gap-3 text-sm text-amber-900"
                        role="alert"
                        data-testid="maintenance-active-banner"
                    >
                        <Wrench className="h-4 w-4 flex-shrink-0 text-amber-700" />
                        <div className="flex-1">
                            <span className="font-semibold">Wartungsmodus aktiv:</span>{' '}
                            <span>Die öffentliche Seite und das Mitglieder-Portal sind aktuell offline.</span>
                        </div>
                        <Link
                            to="/admin/settings/general"
                            className="text-xs font-semibold text-amber-800 hover:text-amber-900 underline whitespace-nowrap"
                            data-testid="maintenance-banner-link"
                        >
                            Zu den Einstellungen
                        </Link>
                    </div>
                )}

                <main className="p-6">
                    {children}
                </main>
                <AppFooter />
            </div>
            <MyProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
            <ReportIssueDialog open={reportOpen} onOpenChange={setReportOpen} />
        </div>
    );
}
